// Code values of the offline card search (W5, mtg-api spec 2026-10-23
// section S and A2): AST fields and operators, modes, classifications,
// IndexedDB names, worker message kinds. Never text a user reads; the
// literal guard skips *.codes.ts files by design (eslint.i18n.config.js).

export const FIELD = {
  text: 'text',
  name: 'name',
  oracle: 'oracle',
  type: 'type',
  color: 'color',
  identity: 'identity',
  mv: 'mv',
  rarity: 'rarity',
  is: 'is',
  legal: 'legal',
} as const
export type Field = (typeof FIELD)[keyof typeof FIELD]

export const OP = {
  contains: 'contains',
  exact: 'exact',
  /** Plain mode with every word dropped: the whole string as a name prefix. */
  prefix: 'prefix',
  eq: '=',
  ne: '!=',
  lt: '<',
  le: '<=',
  gt: '>',
  ge: '>=',
} as const
export type Op = (typeof OP)[keyof typeof OP]

export const IS_VALUE = { commander: 'commander', gamechanger: 'gamechanger' } as const
export const COMMANDER_FORMAT = 'commander'
export const LEGAL = 'legal'

export const MODE = { syntax: 'syntax', plain: 'plain' } as const
export type QueryMode = (typeof MODE)[keyof typeof MODE]

export const EXPECT = { ok: 'ok', needsConnection: 'needs_connection', invalid: 'invalid' } as const
export type Classification = (typeof EXPECT)[keyof typeof EXPECT]

export const RARITY_ALIASES: Readonly<Record<string, string>> = {
  c: 'common', common: 'common',
  u: 'uncommon', uncommon: 'uncommon',
  r: 'rare', rare: 'rare',
  m: 'mythic', mythic: 'mythic',
  s: 'special', special: 'special',
  b: 'bonus', bonus: 'bonus',
}
export const RARITY_ORDER: Readonly<Record<string, number>> = { common: 0, uncommon: 1, rare: 2, mythic: 3 }

export const COLOR_LETTERS: Readonly<Record<string, string>> = { w: 'W', u: 'U', b: 'B', r: 'R', g: 'G' }
export const COLOR_WORDS: Readonly<Record<string, string>> = { white: 'W', blue: 'U', black: 'B', red: 'R', green: 'G' }
export const COLORLESS_WORDS: readonly string[] = ['c', 'colorless', '0']
export const WUBRG: readonly string[] = ['W', 'U', 'B', 'R', 'G']

/** The searchable `role:` buckets of the reference (app/scryfall.py ROLE_BUCKETS). */
export const ROLE_BUCKETS: readonly string[] = [
  'ramp', 'removal', 'card_draw', 'tutor', 'board_wipe', 'counterspell', 'recursion', 'protection', 'discard',
  'sacrifice_outlet', 'lifegain', 'graveyard_hate', 'mana_doubler', 'extra_turns',
]
/** Atoms the reference refuses when negated (a lagging snapshot). */
export const NEGATION_UNSAFE_PREFIXES: readonly string[] = ['otag:', 'oracletag:', 'function:', 'role:']
/** is: atoms the reference implements but the offline subset does not. */
export const IS_ONLINE_ONLY: readonly string[] = ['is:hybrid', 'is:flip', 'is:dfc', 'is:mdfc', 'is:tdfc', 'is:permanent']

/** The reference's refusal messages (app/card_filter.py, app/scryfall.py). */
export const REASON = {
  empty: 'the query is empty',
  tooLong: (max: number) => `the query is longer than ${max} characters`,
  dangling: (tok: string) => `the query ends with ${pyRepr(tok)}, which has nothing after it`,
  regex: 'regular expressions (o:/.../) are not allowed here; use o:"text" instead',
  unsupported: (what: string) => `unsupported query term: ${what}`,
  unbalanced: "unbalanced '(' in query",
  emptyGroup: 'empty group in query',
  emptyBefore: "empty branch before 'or'",
  emptyAfter: "empty branch after 'or'",
  danglingNeg: "dangling '-' in query",
  unconsumed: (tok: string) => `unconsumed token ${pyRepr(tok)}`,
} as const

/** Python's repr() of a str, enough for the reasons above. */
export function pyRepr(s: string): string {
  if (s.includes("'") && !s.includes('"')) return `"${s}"`
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
}

export const ORDER = { edhrec: 'edhrec', usd: 'usd', cmc: 'cmc' } as const
export type SearchOrder = (typeof ORDER)[keyof typeof ORDER]

// --- storage (C5: public data lives in IndexedDB `cb-offline-shared`) ---
export const SHARED_DB = 'cb-offline-shared'
export const SHARED_DB_VERSION = 1
export const STORE_META = 'meta'
export const STORE_DATA = 'cards'
export const META_INSTALLED = 'installed'
export const META_CHECK = 'check'
/** Bumps when the persisted index layout changes (independent of the A2 parser_version). */
export const INDEX_FORMAT = 1
export const SUPPORTED_SCHEMA = 1
export const SUPPORTED_PARSER = 1

// --- worker protocol ---
export const MSG = {
  install: 'install',
  load: 'load',
  verify: 'verify',
  search: 'search',
  autocomplete: 'autocomplete',
  suggest: 'suggest',
  clear: 'clear',
} as const
export type MsgKind = (typeof MSG)[keyof typeof MSG]

/** C6 error codes for the card_database item. */
export const OFFLINE_ERROR = {
  storageFull: 'storage_full',
  serverError: 'server_error',
  checksum: 'checksum',
  unsupported: 'unsupported',
  network: 'network',
  /** The installed data failed validation (missing, unreadable, wrong counts): dropped, download again. */
  corrupt: 'corrupt',
} as const
export type OfflineErrorCode = (typeof OFFLINE_ERROR)[keyof typeof OFFLINE_ERROR]

export const STATE = {
  notApplicable: 'not_applicable',
  notStarted: 'not_started',
  /** Installed, its stored bytes not yet confirmed by the worker: never reported ready before. */
  checking: 'checking',
  downloading: 'downloading',
  ready: 'ready',
  stale: 'stale',
  error: 'error',
} as const
export type OfflineState = (typeof STATE)[keyof typeof STATE]

/** Web Lock serializing installs across every window of the installed app. */
export const INSTALL_LOCK = 'cb-offline-cards-install'
export const MANIFEST_PATH = '/cards/offline-data/manifest'
export const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000
export const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000
export const UNMETERED_TYPES: readonly string[] = ['wifi', 'ethernet']
export const STANDALONE_QUERY = '(display-mode: standalone)'
export const QUOTA_EXCEEDED = 'QuotaExceededError'
export const ABORT_ERROR = 'AbortError'
export const SHA_256 = 'SHA-256'
export const GZIP = 'gzip'

/** CardSearchError code for a query the offline subset cannot answer. */
export const NEEDS_CONNECTION = 'needs_connection'
export const INVALID_SYNTAX = 'invalid_syntax'
export const OFFLINE_CURSOR_PREFIX = 'offline:'
export const SYNTAX_SOURCE = 'syntax'
export const AUTOCOMPLETE_ANSWER_KEY = 'autocomplete-answer'
/** React Query networkMode for queries that have an offline answer. */
export const NETWORK_MODE_ALWAYS = 'always' as const
